import { createContext, useState, useCallback, useContext } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AddDislike, GetUserRecommendations } from "../services/backend";
import { AuthContext } from "./auth-context";
import {
  refreshRecommendationsSeed,
} from "../util/recommendationsSeed";

export const ProfileContext = createContext({
  recommendedUsers: null,
  selectedUser: null,
  LikeSelectedProfile: () => {},
  UpdateAllProfiles: () => {},
  GetAllProfiles: async () => {},
  isLoadingRecommendations: true,
});

/** Persist-exclude profiles the RPC returned but Discover UI cannot show (e.g. letter avatars). */
async function excludeUnusableRecommendations(viewerId, rejectedIds) {
  if (!viewerId || !Array.isArray(rejectedIds) || rejectedIds.length === 0) return;
  await Promise.all(
    rejectedIds.map((id) =>
      AddDislike({
        dislikerProfileId: viewerId,
        dislikedProfileId: id,
      }).catch(() => {})
    )
  );
}

function ProfileContextProvider({ children }) {
  const authCtx = useContext(AuthContext);
  const queryClient = useQueryClient();
  const userId = authCtx.authenticatedUser?.id;
  const [selectedUser, setSelectedUser] = useState({});

  const { data: fetchedUsers, isLoading } = useQuery({
    queryKey: ["recommendations", userId],
    queryFn: async () => {
      // New query run = new random order for this Discover load.
      const seed = refreshRecommendationsSeed(userId);
      // Over-fetch: client photo rules are stricter than the RPC, so a raw page
      // of 40 can shrink a lot after filtering.
      const page = await GetUserRecommendations(userId, { limit: 80, offset: 0, seed });
      await excludeUnusableRecommendations(userId, page.rejectedIds);
      return page.items;
    },
    enabled: !!userId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const GetAllProfiles = useCallback(
    async (uid) => {
      const id = uid ?? userId;
      if (id) refreshRecommendationsSeed(id);
      await queryClient.invalidateQueries({ queryKey: ["recommendations", id] });
    },
    [queryClient, userId]
  );

  async function UpdateAllProfiles(user) {
    queryClient.setQueryData(["recommendations", userId], user);
  }

  async function LikeSelectedProfile(usr) {
    if (!Array.isArray(fetchedUsers)) return;
    const updatedUsers = fetchedUsers.map((entry) => {
      if (entry.user?.id === usr.id) {
        return { ...entry, isliked: !entry.isliked };
      }
      return entry;
    });
    queryClient.setQueryData(["recommendations", userId], updatedUsers);
  }

  const value = {
    recommendedUsers: userId ? (isLoading ? null : (fetchedUsers ?? [])) : null,
    selectedUser,
    GetAllProfiles,
    UpdateAllProfiles,
    LikeSelectedProfile,
    isLoadingRecommendations: isLoading,
  };

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

export default ProfileContextProvider;
